/* lrdiy-tankstrap: tank-circumference field for made-to-measure products.
 * - Diameter mode converts to circumference (C = π × d) and fills the circumference property.
 * - Keeps a "Cable to cut (m)" property = circumference + fold-back allowance.
 * - The product form is `novalidate`, so a document-level capture listener stops the
 *   add-to-cart submit (before product-form.js sees it) until the measurement is valid.
 */
if (!customElements.get('tankstrap-measure')) {
  customElements.define(
    'tankstrap-measure',
    class TankStrapMeasure extends HTMLElement {
      connectedCallback() {
        this.formId = this.dataset.formId;
        this.foldback = parseFloat(this.dataset.foldback) || 0;
        this.circ = this.querySelector('[data-field="circumference"] input');
        this.diam = this.querySelector('[data-field="diameter"] input');
        this.diamField = this.querySelector('[data-field="diameter"]');
        this.cable = this.querySelector('[data-cable]');
        this.result = this.querySelector('[data-result]');
        this.resultValue = this.querySelector('[data-result-value]');
        this.resultNote = this.querySelector('[data-result-note]');
        this.error = this.querySelector('[data-error]');
        this.modes = this.querySelectorAll('input[type="radio"]');

        this.modes.forEach((r) => r.addEventListener('change', () => this.setMode(r.value)));
        this.circ.addEventListener('input', () => this.update());
        this.circ.addEventListener('change', () => this.tidy(this.circ));
        if (this.diam) {
          this.diam.addEventListener('input', () => this.fromDiameter());
          this.diam.addEventListener('change', () => { this.tidy(this.diam); this.fromDiameter(); });
        }

        this.onSubmit = this.onSubmit.bind(this);
        document.addEventListener('submit', this.onSubmit, true);
        this.update();
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onSubmit, true);
      }

      setMode(mode) {
        const diameter = mode === 'diameter';
        if (this.diamField) this.diamField.hidden = !diameter;
        if (this.diam) this.diam.disabled = !diameter;
        this.circ.readOnly = diameter;
        this.circ.classList.toggle('is-derived', diameter);
        if (diameter) this.fromDiameter();
        else this.update();
      }

      fromDiameter() {
        const d = parseFloat(this.diam.value);
        this.circ.value = d > 0 ? (Math.PI * d).toFixed(2) : '';
        this.update();
      }

      // Round to 2 decimal places on blur so the order property and the cable length always agree
      tidy(input) {
        const v = parseFloat(input.value);
        if (Number.isFinite(v)) input.value = String(Math.round(v * 100) / 100);
        this.update();
      }

      value() {
        const c = parseFloat(this.circ.value);
        const min = parseFloat(this.circ.min) || 0;
        const max = parseFloat(this.circ.max) || Infinity;
        return c >= min && c <= max ? c : null;
      }

      update() {
        const c = this.value();
        this.hideError();
        if (c) {
          const cable = (c + this.foldback).toFixed(2);
          this.cable.value = cable;
          this.cable.disabled = false;
          this.resultValue.textContent = `${cable} m`;
          this.resultNote.textContent =
            this.foldback > 0 ? `${c.toFixed(2)} m around your tank + ${this.foldback} m for the fold-back` : '';
          this.result.hidden = false;
        } else {
          this.cable.value = '';
          this.cable.disabled = true;
          this.result.hidden = true;
        }
      }

      showError(msg) {
        this.error.textContent = msg;
        this.error.hidden = false;
        this.classList.add('has-error');
      }

      hideError() {
        this.error.hidden = true;
        this.classList.remove('has-error');
      }

      onSubmit(evt) {
        // getAttribute, not .id: Dawn's product form has <input name="id">, which shadows form.id
        if (!evt.target || !evt.target.getAttribute || evt.target.getAttribute('id') !== this.formId) return;
        // runs before product-form.js builds its FormData, so the submitted values are already rounded
        if (this.diam && !this.diam.disabled) { this.tidy(this.diam); this.fromDiameter(); }
        else this.tidy(this.circ);
        if (this.value()) return;
        evt.preventDefault();
        evt.stopImmediatePropagation();
        const usingDiameter = this.diam && !this.diam.disabled;
        this.showError(
          usingDiameter
            ? "Enter your tank's diameter in metres so we can cut the cable to fit."
            : "Enter your tank's circumference in metres so we can cut the cable to fit."
        );
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (usingDiameter ? this.diam : this.circ).focus({ preventScroll: true });
      }
    }
  );
}
